import React, { useState, useMemo } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import moment from 'moment';
import { makeStyles } from '@mui/styles';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TablePagination
} from '@mui/material';

//import { getInitials } from 'helpers';
import { NavLink } from 'react-router-dom';
import { updateSelectedRestaurantIds } from 'store/actions/restaurant'
import { useDispatch, useSelector } from 'react-redux';
import { ListCard, ListCardField, ListCards } from 'components';
import useCompactLayout from 'hooks/useCompactLayout';


const useStyles = makeStyles(theme => ({
  root: {},
  content: {
    padding: 0
  },
  inner: {
   // minWidth: 1050
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  avatar: {
    marginRight: theme.spacing(2)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  textLink: {
		textDecoration: 'underline',
	},
}));

const SubscriptionsTable = props => {
  const { className, restaurants, ...rest } = props;
  const dispatch = useDispatch();
  const isCompact = useCompactLayout();
  const classes = useStyles();

  const [selectedRestaurants, setSelectedRestaurants] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const updateRestaurantIds = (ids) => dispatch(updateSelectedRestaurantIds(ids));
  const restaurantFilter = useSelector((state) => state.restaurant.filter);



  const handleSelectOne = (event, urlIdentifier) => {
    const selectedIndex = selectedRestaurants.indexOf(urlIdentifier);
    let newSelectedRestaurants = [];

    if (selectedIndex === -1) {
      newSelectedRestaurants = newSelectedRestaurants.concat(selectedRestaurants, urlIdentifier);
    } else if (selectedIndex === 0) {
      newSelectedRestaurants = newSelectedRestaurants.concat(selectedRestaurants.slice(1));
    } else if (selectedIndex === selectedRestaurants.length - 1) {
      newSelectedRestaurants = newSelectedRestaurants.concat(selectedRestaurants.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedRestaurants = newSelectedRestaurants.concat(
        selectedRestaurants.slice(0, selectedIndex),
        selectedRestaurants.slice(selectedIndex + 1)
      );
    }

    setSelectedRestaurants(newSelectedRestaurants);
    updateRestaurantIds(newSelectedRestaurants);
  };

  const handlePageChange = (event, page) => {
    setPage(page);
  };

  const handleRowsPerPageChange = event => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const statusChipColors = {
    active:             { bg: '#4caf50', text: '#fff' },
    canceled:           { bg: '#ef5350', text: '#fff' },
    past_due:           { bg: '#ff9800', text: '#fff' },
    trialing:           { bg: '#2196f3', text: '#fff' },
    incomplete:         { bg: '#9e9e9e', text: '#fff' },
    incomplete_expired: { bg: '#616161', text: '#fff' },
    unpaid:             { bg: '#f44336', text: '#fff' },
    paused:             { bg: '#78909c', text: '#fff' },
  };

  const getSubscriptionStatus = (restaurant) => {
    if (!restaurant.subscriptions || restaurant.subscriptions.length === 0) {
      return 'none';
    }
    const subscription = restaurant.subscriptions[0];
    return subscription?.status || 'none';
  };

  const filteredRestaurants = useMemo(() => {
    let filtered = restaurants || [];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => getSubscriptionStatus(r) === statusFilter);
    }

    if (restaurantFilter && restaurantFilter.trim() !== '') {
      const search = restaurantFilter.toLowerCase();
      filtered = filtered.filter(r =>
        (r.name && r.name.toLowerCase().includes(search))
        || (r.altName && r.altName.toLowerCase().includes(search))
        || (r.restaurantAdmin && r.restaurantAdmin.toLowerCase().includes(search))
        || (r.description && r.description.toLowerCase().includes(search))
        || (r.urlIdentifier && r.urlIdentifier.toLowerCase().includes(search))
      );
    }

    return filtered;
  }, [restaurants, statusFilter, restaurantFilter]);

  const getSubscriptionStart = (restaurant) => {
    if (!restaurant.subscriptions || restaurant.subscriptions.length === 0) {
      return '';
    }
    const subscription = restaurant.subscriptions.find(subscription => subscription.status === 'active');
    if (subscription && subscription.current_period_start) {
      return moment(new Date(subscription.current_period_start * 1000)).format('DD/MM/YYYY');
    }

  };


  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  // One slice, shared by both layouts, so page state survives a resize.
  const pagedRestaurants = filteredRestaurants.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const statusChip = (restaurant) =>
    getSubscriptionStatus(restaurant) !== 'none' ? (
      <Chip
        label={getSubscriptionStatus(restaurant)}
        size="small"
        sx={{
          backgroundColor: statusChipColors[getSubscriptionStatus(restaurant)]?.bg || '#9e9e9e',
          color: statusChipColors[getSubscriptionStatus(restaurant)]?.text || '#fff',
          fontWeight: 600,
          fontSize: '0.7rem',
          textTransform: 'capitalize',
        }}
      />
    ) : null;

  const renderCards = () => (
    <ListCards count={filteredRestaurants.length} noun="subscription" empty="No subscriptions to show.">
      {pagedRestaurants.map(restaurant => (
        <ListCard
          key={restaurant.urlIdentifier}
          data-testid="subscription-card"
          header={
            <>
              <Checkbox
                checked={selectedRestaurants.indexOf(restaurant.urlIdentifier) !== -1}
                color="primary"
                onChange={event => handleSelectOne(event, restaurant.urlIdentifier)}
                size="small"
                inputProps={{ 'aria-label': `select ${restaurant.name}` }}
              />
              <NavLink to={`/restaurant/${restaurant.urlIdentifier}`} style={{ flex: 1, minWidth: 0 }}>
                <div className={classes.textLink}>
                  <Typography variant="body1">{restaurant.name}</Typography>
                </div>
              </NavLink>
              {statusChip(restaurant)}
            </>
          }
        >
          <ListCardField label="Alt name" value={restaurant.altName} />
          <ListCardField label="Id" value={restaurant.urlIdentifier} />
          <ListCardField label="Price" value={restaurant.serviceMonthlyPrice ? `$${restaurant.serviceMonthlyPrice}` : ''} />
          <ListCardField label="Started" value={getSubscriptionStart(restaurant)} />
        </ListCard>
      ))}
    </ListCards>
  );

  return (
    <Card
      {...rest}
      className={clsx(classes.root, className)}
    >
      <Box sx={{ px: 2, pt: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Subscription Status</InputLabel>
          <Select
            value={statusFilter}
            label="Subscription Status"
            onChange={handleStatusFilterChange}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="trialing">Trialing</MenuItem>
            <MenuItem value="past_due">Past Due</MenuItem>
            <MenuItem value="canceled">Canceled</MenuItem>
            <MenuItem value="unpaid">Unpaid</MenuItem>
            <MenuItem value="incomplete">Incomplete</MenuItem>
            <MenuItem value="incomplete_expired">Incomplete Expired</MenuItem>
            <MenuItem value="paused">Paused</MenuItem>
            <MenuItem value="none">No Subscription</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <CardContent className={classes.content}>
        {isCompact ? renderCards() : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    {/* <Checkbox
                      checked={selectedRestaurants.length === restaurants.length}
                      color="primary"
                      indeterminate={
                        selectedRestaurants.length > 0 &&
                        selectedRestaurants.length < restaurants.length
                      }
                      onChange={handleSelectAll}
                    /> */}
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Alt Name</TableCell>
                  <TableCell>Restaurant Id</TableCell>
                  <TableCell>Current Price</TableCell>
                  <TableCell>Subscription Status</TableCell>
                  <TableCell>Subscription Start</TableCell>
              
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRestaurants.map(restaurant => (
                  <TableRow
                    className={classes.tableRow}
                    hover
                    key={restaurant.urlIdentifier}
                    selected={selectedRestaurants.indexOf(restaurant.urlIdentifier) !== -1}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRestaurants.indexOf(restaurant.urlIdentifier) !== -1}
                        color="primary"
                        onChange={event => handleSelectOne(event, restaurant.urlIdentifier)}
                        value="true"
                      />
                    </TableCell>
                    <TableCell >
                      <NavLink to={`/restaurant/${restaurant.urlIdentifier}`}>
                        <div className={classes.textLink}>
                          <Typography variant="body1">{restaurant.name}</Typography>
                        </div>
                      </NavLink>
                    </TableCell>
                    <TableCell>{restaurant.altName ? restaurant.altName : ''}</TableCell>
                    <TableCell>{restaurant.urlIdentifier}</TableCell>
                    <TableCell>{restaurant.serviceMonthlyPrice ? `$${restaurant.serviceMonthlyPrice}`: ''}</TableCell>
                    <TableCell>{statusChip(restaurant)}</TableCell>
                    <TableCell>
                      {getSubscriptionStart(restaurant)}
                    </TableCell>
                  
                  
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
        )}
      </CardContent>
      <CardActions className={classes.actions}>
        <TablePagination
          component="div"
          count={filteredRestaurants.length}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </CardActions>
    </Card>
  );
};

SubscriptionsTable.propTypes = {
  className: PropTypes.string,
  restaurants: PropTypes.array.isRequired
};

export default SubscriptionsTable;
